import { useEffect, useState } from "react";
import SmartInspectRoiForm from "../consultant/SmartInspectRoiForm";
import {
  siPublicConfig, siPublicCreate, siPublicUpdate, siPublicComplete, siPublicGet, siPublicPackages, siPublicCalculate,
} from "../lib/smartInspectApi";
import { Loader2 } from "lucide-react";
import { Toaster } from "../components/ui/sonner";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const LS_KEY = "smartinspect_roi_resume";

export default function SmartInspectRoiPage() {
  const [config, setConfig] = useState(null);
  const [benchmarks, setBenchmarks] = useState({});
  const [workingHours, setWorkingHours] = useState(8);
  const [packages, setPackages] = useState([]);
  const [assessment, setAssessment] = useState({ answers: {}, current_step: 1, status: "draft" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [cfg, pkgs] = await Promise.all([siPublicConfig(), siPublicPackages().catch(() => [])]);
        setConfig(cfg.steps);
        setBenchmarks(cfg.regional_benchmarks || {});
        setWorkingHours(cfg.working_hours_per_day || 8);
        setPackages(pkgs || []);
        // Resume if a saved link exists.
        const saved = JSON.parse(localStorage.getItem(LS_KEY) || "null");
        if (saved?.id && saved?.token) {
          try {
            const a = await siPublicGet(saved.id, saved.token);
            setAssessment(a);
          } catch { localStorage.removeItem(LS_KEY); }
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const onPersist = async (partialAnswers, { step, complete }) => {
    let a = assessment;
    if (!a?.id) {
      a = await siPublicCreate(partialAnswers);
      localStorage.setItem(LS_KEY, JSON.stringify({ id: a.id, token: a.resume_token }));
    } else if (complete) {
      a = await siPublicComplete(a.id, a.resume_token, partialAnswers);
    } else {
      a = await siPublicUpdate(a.id, a.resume_token, partialAnswers, step);
    }
    setAssessment(a);
    return a;
  };

  const onCalculate = async () => {
    const a = assessment;
    if (!a?.id) return;
    const res = await siPublicCalculate(a.id, a.resume_token, {});
    setAssessment((prev) => ({ ...prev, roi_result: res.roi_result }));
    return res.roi_result;
  };

  if (loading || !config) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Toaster position="top-center" />
      <Header />
      <main className="flex-1 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 text-[#025979] text-xs font-semibold uppercase tracking-wide mb-2">
              WarehouseX SmartIntegrations
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#111827]" style={{ fontFamily: "Chivo, sans-serif" }}>
              SmartInspect ROI Calculator
            </h1>
            <p className="text-slate-500 mt-2 max-w-2xl text-sm sm:text-base leading-relaxed">
              Estimate the business value of digitising your inspection process using SmartInspect.
            </p>
          </div>
          <SmartInspectRoiForm config={config} benchmarks={benchmarks} workingHours={workingHours} packages={packages} assessment={assessment} onPersist={onPersist} onCalculate={onCalculate} />
        </div>
      </main>
      <Footer />
    </div>
  );
}
